import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "h-[180px] bg-gray-100 rounded-xl animate-pulse" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "h-[420px] bg-gray-100 rounded-xl animate-pulse" }, null, -1)
const _hoisted_4 = {
  key: 2,
  class: "flex items-center justify-center py-16 text-sm text-[#7f7f8e]"
}

import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import { ScheduleConflictsSummary } from '@/Network/Types/Responses/HandoverInviteResponses'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'
import HandoverFlowStrip from './components/HandoverFlowStrip.vue'
import PackageDetailCard from './components/PackageDetailCard.vue'
import HandoverConfirmationModal from './components/HandoverConfirmationModal.vue'
import KillSwitchConfirmModal from './components/KillSwitchConfirmModal.vue'
import CandidatesModal from './components/CandidatesModal.vue'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'


export default /*#__PURE__*/_defineComponent({
  __name: 'HandoverEvaluation',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const handoverStore = useHandoverStore()
const headerStore = useHeaderStore()
const notificationStore = useNotificationStore()
const { flags } = useFeatureFlags()

const {
  currentEvaluation: evaluation,
  isLoadingEvaluation,
  isProcessing
} = storeToRefs(handoverStore)

const packageId = computed(() => Number(route.params.packageId))

const showApproveModal = ref(false)
const showDeclineModal = ref(false)
const showCandidatesModal = ref(false)
const showKillSwitchModal = ref(false)

const killSwitchImpact = ref<{
  hasConflicts: boolean
  originDoctor: { id: number; name: string; crm: string; avatarUrl: string | null }
  affectedShiftsCount: number
  affectedShiftsDescription: string
  activeAcceptanceCount: number
  candidatesCount: number
} | null>(null)

const scheduleConflicts = ref<ScheduleConflictsSummary | null>(null)
const consecutiveHoursViolations = ref<ConsecutiveHoursViolation[]>([])

const approveMessage = computed(() => {
  if (!evaluation.value) return ''
  const stationCount = evaluation.value.package.stations.length
  const requester = getFirstAndSecondName(evaluation.value.package.requester.name)
  const candidate = getFirstAndSecondName(evaluation.value.currentCandidate?.doctor.name ?? '')
  return `Tem certeza que deseja aprovar a troca de ${stationCount} plantões do Dr(a). ${requester} para o Dr(a). ${candidate}? Essa ação não poderá ser desfeita`
})

const isLastCandidate = computed(() => {
  return evaluation.value?.totalInQueue === 1
})

const isExpired = computed(() => {
  if (!evaluation.value) return false
  return new Date(evaluation.value.package.expiresAt).getTime() < Date.now()
})

const declineMessage = computed(() => {
  if (!evaluation.value) return ''
  const candidate = getFirstAndSecondName(evaluation.value.currentCandidate?.doctor.name ?? '')
  if (isLastCandidate.value) {
    return `Tem certeza que deseja reprovar o(a) Dr(a). ${candidate}? Esse é o último candidato e caso reprovado, a troca será cancelada por completo`
  }
  return `Tem certeza que deseja reprovar o(a) Dr(a). ${candidate}? ${evaluation.value.message}`
})

const goBack = () => {
  router.push(approvalsQueueLocation('handoverApprovals'))
}

const markRelatedNotificationAsRead = async () => {
  const targetUrl = `/aprovacoes/trocas/${packageId.value}/analisar`
  const notification = notificationStore.notifications.find((n) => n.targetUrl === targetUrl)
  if (notification && !notification.isRead) {
    await notificationStore.markAsRead(notification.id)
  }
}

const handleViewAllCandidates = () => {
  showCandidatesModal.value = true
}

const handleApprove = async () => {
  if (!evaluation.value?.currentCandidate || isExpired.value) return
  const preview = await handoverStore.fetchApprovePreview(packageId.value)
  if (!preview) return

  scheduleConflicts.value = preview.scheduleConflicts
  consecutiveHoursViolations.value = flags.enabledConsecutiveHoursLimit
    ? (preview.consecutiveHoursViolations ?? [])
    : []

  // Bloqueio duro: com violação de jornada, abre o modal de aprovação apenas para
  // exibir o motivo — o botão de confirmar fica desabilitado. Não passa pelo kill switch.
  if (consecutiveHoursViolations.value.length > 0) {
    showApproveModal.value = true
    return
  }

  if (preview.hasConflicts && preview.conflictingSummary) {
    killSwitchImpact.value = {
      hasConflicts: true,
      originDoctor: {
        id: 0,
        name: preview.conflictingSummary.originDoctor.name,
        crm: preview.conflictingSummary.originDoctor.crm,
        avatarUrl: null
      },
      affectedShiftsCount: preview.conflictingSummary.affectedShiftsCount,
      affectedShiftsDescription: preview.conflictingSummary.affectedShiftsDescription,
      activeAcceptanceCount: preview.conflictingSummary.activeAcceptanceCount,
      candidatesCount: preview.conflictingSummary.candidatesCount
    }
    showKillSwitchModal.value = true
  } else {
    showApproveModal.value = true
  }
}

const handleDecline = () => {
  if (!evaluation.value?.currentCandidate || isExpired.value) return
  showDeclineModal.value = true
}

/**
 * O preview pode passar limpo e a escrita ser recusada depois (revalidação dentro da
 * transação). Quando a recusa traz violações de jornada, cai no mesmo bloqueio duro do
 * preview: modal de aprovação aberto só para mostrar o motivo, com o confirmar desabilitado.
 */
const applyApprovalBlock = () => {
  const violations = handoverStore.approvalConsecutiveHoursViolations
  if (violations.length === 0) return
  consecutiveHoursViolations.value = violations
  showKillSwitchModal.value = false
  showApproveModal.value = true
}

const confirmApprove = async () => {
  if (!evaluation.value) return
  const success = await handoverStore.adminApproveFromEvaluation(evaluation.value.package.id)
  if (!success) {
    applyApprovalBlock()
    return
  }
  showApproveModal.value = false
  await markRelatedNotificationAsRead()
  await handoverStore.fetchPendingApprovals()
  goBack()
}

const confirmKillSwitchApprove = async (reason: string) => {
  if (!evaluation.value) return
  const success = await handoverStore.adminApproveFromEvaluation(
    evaluation.value.package.id,
    reason
  )
  if (!success) {
    applyApprovalBlock()
    return
  }
  showKillSwitchModal.value = false
  await markRelatedNotificationAsRead()
  await handoverStore.fetchPendingApprovals()
  goBack()
}

const confirmDecline = async () => {
  if (!evaluation.value) return
  const result = await handoverStore.adminDeclineFromEvaluation(evaluation.value.package.id)
  showDeclineModal.value = false
  if (result === null) {
    await markRelatedNotificationAsRead()
    await handoverStore.fetchPendingApprovals()
    goBack()
  }
}

onMounted(async () => {
  headerStore.setTitle(
    'Avaliação de Troca',
    'Avalie e aprove solicitações de trocas e repasses de plantão',
    approvalsQueueBreadcrumbs('handoverApprovals')
  )

  await handoverStore.fetchAdminQueueItem(packageId.value)

  if (
    evaluation.value?.package.status &&
    evaluation.value.package.status !== PackageStatus.IN_ADMIN_QUEUE
  ) {
    await markRelatedNotificationAsRead()
    goBack()
  }
})

onUnmounted(() => {
  handoverStore.setCurrentEvaluation(null)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isLoadingEvaluation))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _hoisted_2,
          _hoisted_3
        ], 64))
      : (_unref(evaluation))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(HandoverFlowStrip, {
              requester: _unref(evaluation).package.requester,
              candidate: _unref(evaluation).currentCandidate,
              message: _unref(evaluation).message,
              "total-invites": _unref(evaluation).package.invites.length,
              "is-expired": isExpired.value,
              "is-processing": _unref(isProcessing),
              onApprove: handleApprove,
              onDecline: handleDecline,
              onViewAllCandidates: handleViewAllCandidates
            }, null, 8, ["requester", "candidate", "message", "total-invites", "is-expired", "is-processing"]),
            _createVNode(PackageDetailCard, {
              "package-data": _unref(evaluation).package
            }, null, 8, ["package-data"])
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, " Nenhuma avaliação encontrada ")),
    _createVNode(HandoverConfirmationModal, {
      modelValue: showApproveModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showApproveModal).value = $event)),
      variant: "approve",
      title: 'Aprovar troca de plantões?',
      message: approveMessage.value,
      loading: _unref(isProcessing),
      "schedule-conflicts": scheduleConflicts.value,
      "consecutive-hours-violations": consecutiveHoursViolations.value,
      onConfirm: confirmApprove
    }, null, 8, ["modelValue", "message", "loading", "schedule-conflicts", "consecutive-hours-violations"]),
    _createVNode(HandoverConfirmationModal, {
      modelValue: showDeclineModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showDeclineModal).value = $event)),
      variant: "decline",
      title: 'Reprovar candidato?',
      message: declineMessage.value,
      loading: _unref(isProcessing),
      onConfirm: confirmDecline
    }, null, 8, ["modelValue", "message", "loading"]),
    (_unref(evaluation) && _unref(evaluation).currentCandidate && killSwitchImpact.value)
      ? (_openBlock(), _createBlock(KillSwitchConfirmModal, {
          key: 3,
          modelValue: showKillSwitchModal.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showKillSwitchModal).value = $event)),
          "package-data": _unref(evaluation).package,
          candidate: _unref(evaluation).currentCandidate,
          impact: killSwitchImpact.value,
          loading: _unref(isProcessing),
          "schedule-conflicts": scheduleConflicts.value,
          onConfirm: confirmKillSwitchApprove
        }, null, 8, ["modelValue", "package-data", "candidate", "impact", "loading", "schedule-conflicts"]))
      : _createCommentVNode("", true),
    (_unref(evaluation))
      ? (_openBlock(), _createBlock(CandidatesModal, {
          key: 4,
          modelValue: showCandidatesModal.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showCandidatesModal).value = $event)),
          invites: _unref(evaluation).package.invites
        }, null, 8, ["modelValue", "invites"]))
      : _createCommentVNode("", true)
  ]))
}
}

})